import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiArrowLeft,
  FiSend,
  FiClock,
  FiAlertCircle,
  FiSearch,
  FiLock,
  FiCheckCircle,
  FiPlus,
} from "react-icons/fi";
import DashboardLayout from "../../UI/Layout/DashboardLayout";
import { fetchConvo, fetchMessages, replyToMessage } from "./instagramApi";
import styles from "./Instagram.module.css";

// Polling cadence: conversation list refreshes less often than the
// active thread, since new messages matter more than new thread metadata.
const CONVERSATIONS_POLL_MS = 15000;
const MESSAGES_POLL_MS = 4000;

// How long a locally-sent (optimistic) message is considered "unconfirmed"
// and kept in the UI until a matching message shows up from the server.
const RECONCILE_WINDOW_MS = 5 * 60 * 1000;

const InstagramConversation = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Both success and error banners are transient — clear them a few
  // seconds after they appear instead of leaving them on screen.
  useEffect(() => {
    if (!successMessage) return;

    const timer = setTimeout(() => setSuccessMessage(""), 3500);

    return () => clearTimeout(timer);
  }, [successMessage]);

  useEffect(() => {
    if (!errorMessage) return;

    const timer = setTimeout(() => setErrorMessage(""), 4500);

    return () => clearTimeout(timer);
  }, [errorMessage]);
  const [replyInputs, setReplyInputs] = useState({});
  const [conversationSearchQuery, setConversationSearchQuery] = useState("");
  const [isMobileChatOpen, setIsMobileChatOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const loginData = JSON.parse(localStorage.getItem("login"));
  const userId = loginData?.user?.userId;
  const isConnected = localStorage.getItem("instagram_connected") === "true";
  const storedInstagramUsername = localStorage.getItem("instagramUsername");
  const [activeAccountId, setActiveAccountId] = useState(
    () => localStorage.getItem("instagramActiveAccountId") || "all",
  );
  const scopedAccountId =
    activeAccountId && activeAccountId !== "all" ? activeAccountId : undefined;

  const [connectedAccounts, setConnectedAccounts] = useState(() => {
    try {
      const saved = localStorage.getItem("instagramAccounts");
      const parsed = saved ? JSON.parse(saved) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  const handleSelectAccount = (accountId) => {
    setActiveAccountId(accountId);
    localStorage.setItem("instagramActiveAccountId", accountId);
    window.dispatchEvent(
      new CustomEvent("instagram-account-changed", {
        detail: { accountId },
      }),
    );
  };

  useEffect(() => {
    const sync = (accountId) => {
      if (!accountId) return;
      setActiveAccountId(accountId);
    };
    const syncAccounts = (accounts) => {
      if (!Array.isArray(accounts)) return;
      setConnectedAccounts(accounts);
    };
    const onCustom = (e) => sync(e.detail?.accountId);
    const onAccountsCustom = (e) => syncAccounts(e.detail?.accounts);
    const onStorage = (e) => {
      if (e.key === "instagramActiveAccountId" && e.newValue) sync(e.newValue);
      if (e.key === "instagramAccounts") {
        try {
          syncAccounts(e.newValue ? JSON.parse(e.newValue) : []);
        } catch {
          // Ignore malformed storage payloads.
        }
      }
    };
    window.addEventListener("instagram-account-changed", onCustom);
    window.addEventListener("instagram-accounts-changed", onAccountsCustom);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("instagram-account-changed", onCustom);
      window.removeEventListener(
        "instagram-accounts-changed",
        onAccountsCustom,
      );
      window.removeEventListener("storage", onStorage);
    };
  }, []);


  useEffect(() => {
    if (connectedAccounts.length === 0) return;

    const stillExists =
      activeAccountId !== "all" &&
      connectedAccounts.some(
        (account) =>
          String(account.id) === String(activeAccountId) ||
          String(account.instaUserId) === String(activeAccountId),
      );

    if (!stillExists) {
      handleSelectAccount(connectedAccounts[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAccountId, connectedAccounts]);

  const stickyHeaderRef = useRef(null);
  const [stickyHeaderOffset, setStickyHeaderOffset] = useState(64);
  const [fixedBarHeight, setFixedBarHeight] = useState(64);
  const [fixedBarLeft, setFixedBarLeft] = useState(0);
  const [fixedBarWidth, setFixedBarWidth] = useState("100%");

  useEffect(() => {
    const measure = () => {
      const globalNavbar = document.querySelector(".navbar.dashboard-header");
      if (globalNavbar) {
        const top = Math.max(0, Math.ceil(globalNavbar.getBoundingClientRect().bottom));
        if (top > 0) setStickyHeaderOffset(top);
      }
      const mainEl = document.querySelector(".main");
      if (mainEl) {
        const r = mainEl.getBoundingClientRect();
        setFixedBarLeft(Math.max(0, Math.round(r.left)));
        setFixedBarWidth(`${Math.round(r.width)}px`);
      }
      if (stickyHeaderRef.current) {
        const h = Math.ceil(stickyHeaderRef.current.getBoundingClientRect().height);
        if (h > 0) setFixedBarHeight(h);
      }
    };
    measure();
    const raf = requestAnimationFrame(measure);
    const t = setTimeout(measure, 120);
    const globalNavbar = document.querySelector(".navbar.dashboard-header");
    const mainEl = document.querySelector(".main");
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro && globalNavbar) ro.observe(globalNavbar);
    if (ro && mainEl) ro.observe(mainEl);
    if (ro && stickyHeaderRef.current) ro.observe(stickyHeaderRef.current);
    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
      if (ro) ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [connectedAccounts.length]);

  const handleConnectInstagram = () => {
    const INSTAGRAM_APP_ID = process.env.REACT_APP_INSTAGRAM_APP_ID;
    const INSTAGRAM_PERMISSION = process.env.REACT_APP_INSTAGRAM_PERMISSION;
    const INSTAGRAM_AUTH_ENDPOINT = process.env.REACT_APP_INSTAGRAM_AUTH_ENDPOINT;
    const INSTAGRAM_REDIRECT_URI = process.env.REACT_APP_INSTAGRAM_REDIRECT_URI;
    if (!userId || !INSTAGRAM_APP_ID) return;
    try {
      window.location.assign(
        `${INSTAGRAM_AUTH_ENDPOINT}?force_reauth=true&client_id=${encodeURIComponent(
          INSTAGRAM_APP_ID,
        )}&redirect_uri=${encodeURIComponent(
          INSTAGRAM_REDIRECT_URI,
        )}&response_type=code&scope=${encodeURIComponent(
          INSTAGRAM_PERMISSION,
        )}&state=${encodeURIComponent(userId)}`,
      );
    } catch {
      // ignore
    }
  };

  // Refs mirror the latest state so polling intervals (set up once) always
  // read fresh values instead of stale closures from when they were created.
  const conversationsRef = useRef(conversations);
  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  const selectedConversationIdRef = useRef(selectedConversationId);
  useEffect(() => {
    selectedConversationIdRef.current = selectedConversationId;
  }, [selectedConversationId]);

  const normalizeConversations = (payload) => {
    const result = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.data?.data)
          ? payload.data.data
          : [];

    return result.map((item) => {
      const participants = Array.isArray(item.participants?.data)
        ? item.participants.data
        : [];
      const participantNames = participants
        .map((participant) => participant?.username || participant?.id)
        .filter(Boolean);
      const participantLabel = participantNames.join(", ");
      const recipient =
        participants.find(
          (participant) =>
            storedInstagramUsername &&
            participant?.username &&
            participant.username !== storedInstagramUsername,
        ) || null;

      return {
        id:
          item.id ||
          item.thread_id ||
          item.conversationId ||
          item.instagram_thread_id ||
          item.media_id ||
          item.commentId ||
          item.post_id ||
          item.key ||
          "unknown",
        name:
          item.name ||
          item.username ||
          recipient?.username ||
          item.profile_name ||
          item.sender_name ||
          item.from_name ||
          item.title ||
          participantLabel ||
          `Conversation ${item.id || ""}`,
        snippet:
          item.snippet ||
          item.last_message ||
          item.text ||
          item.message ||
          item.caption ||
          item.body ||
          participantLabel ||
          "No preview available.",
        timestamp:
          item.timestamp ||
          item.updated_time ||
          item.created_time ||
          item.date ||
          item.time ||
          "",
        unreadCount: item.unread_count || item.unread || item.unseen || 0,
        recipientId:
          item.recipient_id ||
          item.recipient_igsid ||
          item.recipientIgsid ||
          recipient?.id,
        recipientUsername: recipient?.username,
        messages: Array.isArray(item.messages)
          ? item.messages
          : Array.isArray(item.thread)
            ? item.thread
            : Array.isArray(item.comments)
              ? item.comments
              : [],
        raw: item,
      };
    });
  };

  const normalizeMessages = (payload) => {
    const result = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.data)
        ? payload.data
        : [];

    return result
      .map((msg, index) => ({
        id: msg.id || `msg-${index}`,
        text: msg.message || msg.text || msg.caption || msg.body || "",
        username:
          msg.username ||
          msg.from?.username ||
          msg.sender?.username ||
          msg.name ||
          "Unknown",
        from: msg.from,
        created_time: msg.created_time || msg.timestamp || msg.time || "",
        status: "sent",
        //adding attached media if any
        attachments: Array.isArray(msg.attachments?.data)
          ? msg.attachments.data
          : [],
        raw: msg,
      }))
      .sort((a, b) => {
        const aTime = new Date(a.created_time).getTime();
        const bTime = new Date(b.created_time).getTime();

        if (Number.isNaN(aTime) || Number.isNaN(bTime)) {
          return 0;
        }

        return aTime - bTime;
      });
  };
  const reconcileMessages = (existingMessages, serverMessages) => {
    const localOnly = (existingMessages || []).filter((m) => m.localOnly);

    const stillLocalOnly = localOnly.filter((local) => {
      if (local.status === "pending" || local.status === "failed") {
        return true;
      }

      const confirmed = serverMessages.some((serverMsg) => {
        const sameText = serverMsg.text.trim() === (local.text || "").trim();
        const sameSender = serverMsg.username === local.username;
        const localTime = new Date(local.created_time).getTime();
        const serverTime = new Date(serverMsg.created_time).getTime();
        const closeInTime =
          Number.isNaN(localTime) ||
          Number.isNaN(serverTime) ||
          Math.abs(serverTime - localTime) < RECONCILE_WINDOW_MS;

        return sameText && sameSender && closeInTime;
      });

      return !confirmed;
    });

    return [...serverMessages, ...stillLocalOnly].sort((a, b) => {
      const aTime = new Date(a.created_time).getTime();
      const bTime = new Date(b.created_time).getTime();

      if (Number.isNaN(aTime) || Number.isNaN(bTime)) {
        return 0;
      }

      return aTime - bTime;
    });
  };

  const selectedConversation = useMemo(() => {
    if (!conversations.length) {
      return null;
    }

    const found = conversations.find(
      (item) => String(item.id) === String(selectedConversationId),
    );

    return found || conversations[0] || null;
  }, [conversations, selectedConversationId]);

  const visibleConversations = useMemo(() => {
    const query = conversationSearchQuery.trim().toLowerCase();
    if (!query) return conversations;

    return conversations.filter((conversation) => {
      const name = (conversation.name || "").toLowerCase();
      const snippet = (conversation.snippet || "").toLowerCase();
      return name.includes(query) || snippet.includes(query);
    });
  }, [conversations, conversationSearchQuery]);

  // True once the backend flags a conversation as having DM automation
  // switched on (e.g. conversation.raw.automation_active). Hidden by
  // default so nothing is implied that isn't actually configured.
  const isAutomationActive = (conversation) =>
    Boolean(
      conversation?.raw?.automation_active ??
      conversation?.raw?.dm_automation_active ??
      conversation?.raw?.is_automation_active,
    );

  // A sent message is only marked "Automated Response" once the backend
  // tags it as such — otherwise it renders as a normal manual reply.
  const isAutomatedMessage = (message) =>
    Boolean(
      message?.raw?.is_automated ??
      message?.raw?.automated ??
      (message?.raw?.type
        ? String(message.raw.type).toLowerCase() === "auto"
        : false),
    );

  // Bunch consecutive messages from the same sender together so the thread
  // isn't repeating a name + timestamp on every single bubble. Also marks
  // day boundaries so a lightweight date divider can be shown once per day.
  const GROUP_WINDOW_MS = 3 * 60 * 1000;

  const groupedMessages = useMemo(() => {
    const messages = selectedConversation?.messages || [];

    return messages.map((message, index) => {
      const prev = messages[index - 1];
      const next = messages[index + 1];

      const time = new Date(message.created_time).getTime();
      const prevTime = prev ? new Date(prev.created_time).getTime() : null;
      const nextTime = next ? new Date(next.created_time).getTime() : null;

      const closeToPrev =
        prev &&
        !Number.isNaN(time) &&
        !Number.isNaN(prevTime) &&
        Math.abs(time - prevTime) < GROUP_WINDOW_MS;
      const closeToNext =
        next &&
        !Number.isNaN(time) &&
        !Number.isNaN(nextTime) &&
        Math.abs(nextTime - time) < GROUP_WINDOW_MS;

      const groupedWithPrev =
        Boolean(prev) && prev.username === message.username && closeToPrev;
      const groupedWithNext =
        Boolean(next) && next.username === message.username && closeToNext;

      const prevDay = prev ? new Date(prev.created_time).toDateString() : null;
      const day = new Date(message.created_time).toDateString();

      return {
        ...message,
        isFirstInGroup: !groupedWithPrev,
        isLastInGroup: !groupedWithNext,
        showDayDivider: !prev || prevDay !== day,
      };
    });
  }, [selectedConversation]);

  const formatDayDivider = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();

    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();

    if (isToday) return "Today";
    if (isYesterday) return "Yesterday";
    return date.toLocaleDateString(undefined, {
      month: "long",
      day: "numeric",
      year: date.getFullYear() === today.getFullYear() ? undefined : "numeric",
    });
  };

  const currentUser =
    storedInstagramUsername ||
    loginData?.user?.instagram_username ||
    loginData?.user?.username ||
    loginData?.user?.name ||
    "You";

  const loadConversations = async ({ silent = false } = {}) => {
    if (!userId) return;

    if (!isConnected) {
      if (!silent) setErrorMessage("Instagram account is not connected.");
      return;
    }

    if (!silent) {
      setErrorMessage("");
      setSuccessMessage("");
      setLoadingConversations(true);
    }

    try {
      const result = await fetchConvo({ userId, instaUserId: scopedAccountId });
      const payload = result?.data || result;
      const normalized = normalizeConversations(payload);

      setConversations((prev) => {
        // Keep any messages we've already loaded/sent for a thread so a
        // background refresh of the list never blanks an open conversation.
        const prevById = new Map(prev.map((c) => [String(c.id), c]));

        return normalized.map((conversation) => {
          const previous = prevById.get(String(conversation.id));
          if (previous?.messages?.length && !conversation.messages?.length) {
            return { ...conversation, messages: previous.messages };
          }
          return conversation;
        });
      });

      setSelectedConversationId((prev) => {
        if (prev) return prev;
        return normalized.length ? normalized[0].id : null;
      });

      if (!silent) {
        setSuccessMessage(
          normalized.length
            ? "Instagram conversations loaded."
            : "No Instagram conversations available.",
        );
      }
    } catch (error) {
      if (!silent) {
        setErrorMessage(
          error.message || "Unable to fetch Instagram conversations.",
        );
      } else {
        console.warn("Background conversation refresh failed:", error);
      }
    } finally {
      if (!silent) setLoadingConversations(false);
    }
  };

  const loadConversationMessages = async (
    conversation,
    { silent = false } = {},
  ) => {
    const conversationId = conversation?.id || conversation;
    const recipientId = conversation?.recipientId;

    if (!userId || !isConnected || !recipientId) {
      if (!silent) {
        if (!isConnected)
          setErrorMessage("Instagram account is not connected.");
        else if (!recipientId)
          setErrorMessage("Unable to find recipient ID for this conversation.");
      }
      return;
    }

    if (!silent) {
      setErrorMessage("");
      setSuccessMessage("");
      setLoadingMessages((prev) => ({ ...prev, [conversationId]: true }));
    }

    try {
      const result = await fetchMessages({
        userId,
        conversationId,
        recipientId,
        instaUserId: scopedAccountId,
      });

      const payload = result?.data || [];
      const serverMessages = normalizeMessages(payload);

      setConversations((prev) =>
        prev.map((c) => {
          if (String(c.id) !== String(conversationId)) return c;
          return {
            ...c,
            messages: reconcileMessages(c.messages, serverMessages),
          };
        }),
      );

      if (!silent) setSuccessMessage("Conversation messages loaded.");
    } catch (err) {
      if (!silent) {
        setErrorMessage(
          err?.message === "Failed to fetch"
            ? "Unable to fetch messages because the Instagram API request timed out or was blocked by CORS."
            : err?.message || "Unable to fetch conversation messages.",
        );
      } else {
        console.warn("Background message refresh failed:", err);
      }
    } finally {
      if (!silent) {
        setLoadingMessages((prev) => ({ ...prev, [conversationId]: false }));
      }
    }
  };

  // Initial conversation list load, then a silent background refresh loop.
  // Re-runs when the active Instagram account changes — clear the
  // currently open thread and cached list first so the UI doesn't keep
  // showing the previous account's conversation while the new account's
  // list loads in.
  useEffect(() => {
    setSelectedConversationId(null);
    setConversations([]);

    loadConversations();

    const interval = setInterval(() => {
      loadConversations({ silent: true });
    }, CONVERSATIONS_POLL_MS);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAccountId]);

  // Whenever the selected thread changes, load it (showing a spinner only if
  // we don't already have messages for it), then poll it silently.
  useEffect(() => {
    if (!selectedConversationId) return;

    const convo = conversationsRef.current.find(
      (c) => String(c.id) === String(selectedConversationId),
    );

    if (convo) {
      loadConversationMessages(convo, { silent: convo.messages?.length > 0 });
    }

    const interval = setInterval(() => {
      const current = conversationsRef.current.find(
        (c) => String(c.id) === String(selectedConversationIdRef.current),
      );
      if (current) {
        loadConversationMessages(current, { silent: true });
      }
    }, MESSAGES_POLL_MS);

    return () => clearInterval(interval);
  }, [selectedConversationId]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [selectedConversation?.messages.length, selectedConversationId]);

  const formatTimestamp = (value) => {
    if (!value) return "Unknown";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();

    if (isToday) {
      return date.toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
      });
    }
    if (isYesterday) return "Yesterday";

    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: date.getFullYear() === now.getFullYear() ? undefined : "numeric",
    });
  };

  // Full "Aug 6, 2026, 5:54 AM" style string — used as a hover tooltip so the
  // precise time is still one hover away even though the list shows a short form.
  const formatFullTimestamp = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Used under each message bubble — time only, since the day divider
  // above the group already shows the date.
  const formatTimeOnly = (value) => {
    if (!value) return "Unknown";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const handleReplyChange = (conversationId, value) => {
    setReplyInputs((prev) => ({ ...prev, [conversationId]: value }));
  };

  const autoGrowTextarea = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };

  const handleSelectConversation = (conversation) => {
    setIsMobileChatOpen(true);

    if (String(conversation.id) === String(selectedConversationId)) {
      // Already open — treat as an explicit manual refresh of this thread.
      loadConversationMessages(conversation);
      return;
    }

    setSelectedConversationId(conversation.id);
  };

  const updateMessageInConversation = (conversationId, messageId, updates) => {
    setConversations((prev) =>
      prev.map((c) => {
        if (String(c.id) !== String(conversationId)) return c;
        return {
          ...c,
          messages: c.messages.map((m) =>
            m.id === messageId ? { ...m, ...updates } : m,
          ),
        };
      }),
    );
  };

  // Shared by both a fresh send and a retry of a failed send.
  const sendMessageCore = async (conversationId, tempId, text) => {
    const convo = conversations.find(
      (c) => String(c.id) === String(conversationId),
    );
    const recipientIgsid =
      convo?.recipientId ||
      convo?.raw?.recipient_igsid ||
      convo?.raw?.recipientIgsid ||
      convo?.raw?.recipient_id;

    if (!recipientIgsid) {
      updateMessageInConversation(conversationId, tempId, { status: "failed" });
      setErrorMessage("Unable to find recipient IGSID for this conversation.");
      return;
    }

    try {
      await replyToMessage({ userId, recipientIgsid, message: text, instaUserId: scopedAccountId });
      updateMessageInConversation(conversationId, tempId, { status: "sent" });
    } catch (err) {
      updateMessageInConversation(conversationId, tempId, { status: "failed" });
      setErrorMessage(err.message || "Unable to send message.");
    }
  };

  const handleSendMessage = async (conversationId) => {
    const message = (replyInputs[conversationId] || "").trim();
    if (!message) {
      setErrorMessage("Reply message cannot be empty.");
      return;
    }

    setErrorMessage("");
    setSuccessMessage("");

    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const optimisticMessage = {
      id: tempId,
      text: message,
      username: currentUser,
      from: { username: currentUser },
      created_time: new Date().toISOString(),
      status: "pending",
      localOnly: true,
    };

    // Optimistic update: show the message instantly, clear the composer,
    // and let the network request resolve in the background.
    setConversations((prev) =>
      prev.map((c) =>
        String(c.id) === String(conversationId)
          ? { ...c, messages: [...(c.messages || []), optimisticMessage] }
          : c,
      ),
    );
    setReplyInputs((prev) => ({ ...prev, [conversationId]: "" }));
    requestAnimationFrame(autoGrowTextarea);

    await sendMessageCore(conversationId, tempId, message);
  };

  const handleRetryMessage = async (conversationId, tempId, text) => {
    updateMessageInConversation(conversationId, tempId, { status: "pending" });
    await sendMessageCore(conversationId, tempId, text);
  };

  const handleComposerKeyDown = (event, conversationId) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage(conversationId);
    }
  };

  return (
    <DashboardLayout>
      <main className="content">
        <div className={styles.conversationPage}>
          <div
            ref={stickyHeaderRef}
            className={styles.fixedPageHeader}
            style={{
              top: stickyHeaderOffset,
              left: fixedBarLeft,
              width: fixedBarWidth,
            }}
          >
            <div className={styles.fixedPageHeaderInner}>
              <div className={styles.pageHeaderLeft}>
                <Link
                  to="/dashboard/instagram"
                  className={styles.backButtonCompact}
                  aria-label="Back to Instagram"
                >
                  <FiArrowLeft />
                </Link>
                <div className={styles.pageHeaderIcon}>
                  <img src="/img/instagram.png" alt="Instagram" />
                </div>
                <div className={styles.pageHeaderTitleRow}>
                  <h1>Instagram Inbox</h1>
                </div>
              </div>
              <div className={styles.pageHeaderRight}>
                <div className={styles.accountPillScroller}>
                  {connectedAccounts.map((account) => (
                    <button
                      type="button"
                      key={account.id}
                      className={`${styles.accountPill} ${activeAccountId === account.id
                          ? styles.accountPillActive
                          : ""
                        }`}
                      onClick={() => handleSelectAccount(account.id)}
                    >
                      {account.avatarUrl ? (
                        <img
                          src={account.avatarUrl}
                          alt=""
                          className={styles.accountPillAvatar}
                        />
                      ) : (
                        <span className={styles.accountPillAvatarFallback}>
                          {account.username?.[0]?.toUpperCase() || "?"}
                        </span>
                      )}
                      @{account.username}
                      <span className={styles.accountPillStatusDot} />
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className={styles.accountSwitcherConnectButton}
                  onClick={handleConnectInstagram}
                >
                  <FiPlus />
                  Connect IG Account
                </button>
              </div>
            </div>
          </div>
          <div
            className={styles.fixedPageHeaderSpacer}
            style={{ height: fixedBarHeight }}
            aria-hidden="true"
          />


          {successMessage && (
            <div className={styles.successMessage}>{successMessage}</div>
          )}
          {errorMessage && (
            <div className={styles.errorMessage}>{errorMessage}</div>
          )}

          <div
            className={`${styles.conversationGrid} ${isMobileChatOpen ? styles.mobileActiveChat : ""
              }`}
          >
            <section className={styles.conversationListPanel}>
              <div className={styles.dmSearchField}>
                <FiSearch />
                <input
                  type="text"
                  placeholder="Search DMs..."
                  value={conversationSearchQuery}
                  onChange={(event) =>
                    setConversationSearchQuery(event.target.value)
                  }
                />
              </div>

              {loadingConversations ? (
                <div className={styles.conversationEmpty}>Loading...</div>
              ) : conversations.length === 0 ? (
                <div className={styles.conversationEmpty}>
                  No conversations found. Fetch Instagram data first.
                </div>
              ) : visibleConversations.length === 0 ? (
                <div className={styles.conversationEmpty}>
                  No DMs match your search.
                </div>
              ) : (
                <div className={styles.conversationList}>
                  {visibleConversations.map((conversation) => (
                    <button
                      key={conversation.id}
                      type="button"
                      className={`${styles.conversationListItem} ${String(conversation.id) ===
                          String(selectedConversation?.id)
                          ? styles.conversationListItemActive
                          : ""
                        }`}
                      onClick={() => handleSelectConversation(conversation)}
                    >
                      <div className={styles.threadAvatar}>
                        {(conversation.name || "?")
                          .trim()
                          .charAt(0)
                          .toUpperCase()}
                      </div>
                      <div className={styles.threadTextGroup}>
                        <div className={styles.conversationName}>
                          {conversation.name}
                        </div>
                        <div className={styles.conversationSnippet}>
                          {conversation.snippet}
                        </div>
                      </div>
                      <div className={styles.conversationMeta}>
                        {conversation.unreadCount > 0 && (
                          <span className={styles.unreadBadge}>
                            {conversation.unreadCount}
                          </span>
                        )}
                        <span
                          className={styles.conversationTime}
                          title={formatFullTimestamp(conversation.timestamp)}
                        >
                          {formatTimestamp(conversation.timestamp)}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </section>

            <section className={styles.conversationPanel}>
              <div className={styles.conversationPanelHeader}>
                {selectedConversation ? (
                  <>
                    <button
                      type="button"
                      className={styles.mobileBackButton}
                      onClick={() => setIsMobileChatOpen(false)}
                      aria-label="Back to threads"
                    >
                      <FiArrowLeft />
                    </button>

                    <div className={styles.conversationPanelAvatar}>
                      {(selectedConversation.name || "?")
                        .trim()
                        .charAt(0)
                        .toUpperCase()}
                    </div>
                    <div className={styles.conversationPanelHeaderText}>
                      <span className={styles.conversationPanelTitle}>
                        @{selectedConversation.name}
                      </span>

                      {isAutomationActive(selectedConversation) && (
                        <span className={styles.automationActivePill}>
                          <FiCheckCircle />
                          IG DM Automation Active
                        </span>
                      )}
                    </div>
                  </>
                ) : (
                  <span className={styles.conversationPanelTitle}>Inbox</span>
                )}
              </div>

              {!isConnected ? (
                <div className={styles.conversationEmpty}>
                  Instagram account is not connected.
                </div>
              ) : !selectedConversation ? (
                <div className={styles.conversationEmpty}>
                  Select a thread to view messages.
                </div>
              ) : loadingMessages[selectedConversationId] ? (
                <div className={styles.conversationEmpty}>
                  Loading messages...
                </div>
              ) : selectedConversation.messages.length === 0 ? (
                <div className={styles.conversationEmpty}>
                  No messages found in this conversation.
                </div>
              ) : (
                <>
                  <div className={styles.conversationMessages}>
                    {groupedMessages.map((message) => {
                      const isMine = message.username === currentUser;
                      const isPending = message.status === "pending";
                      const isFailed = message.status === "failed";
                      const isAutomated = isMine && isAutomatedMessage(message);
                      const initial = (message.username || "?")
                        .trim()
                        .charAt(0)
                        .toUpperCase();

                      return (
                        <React.Fragment key={message.id}>
                          {message.showDayDivider && (
                            <div className={styles.dayDivider}>
                              <span>
                                {formatDayDivider(message.created_time)}
                              </span>
                            </div>
                          )}

                          <div
                            className={`${styles.messageRow} ${isMine
                                ? styles.messageRowMine
                                : styles.messageRowTheirs
                              } ${message.isFirstInGroup
                                ? styles.messageRowFirst
                                : ""
                              }`}
                          >
                            {!isMine && (
                              <div
                                className={styles.messageAvatar}
                                style={{
                                  visibility: message.isLastInGroup
                                    ? "visible"
                                    : "hidden",
                                }}
                              >
                                {initial}
                              </div>
                            )}

                            <div className={styles.messageBubbleColumn}>
                              {!isMine && message.isFirstInGroup && (
                                <span className={styles.messageAuthor}>
                                  {message.username}
                                </span>
                              )}

                              <div
                                className={`${styles.conversationMessageItem} ${isMine
                                    ? styles.sentMessage
                                    : styles.receivedMessage
                                  } ${isAutomated ? styles.automatedMessage : ""} ${isPending ? styles.messagePending : ""
                                  } ${isFailed ? styles.messageFailed : ""}`}
                              >
                                {isAutomated && (
                                  <div className={styles.automatedBadge}>
                                    <FiLock />
                                    Automated Response
                                  </div>
                                )}

                                <div className={styles.messageBody}>
                                  {message.text && (
                                    <div className={styles.messageText}>
                                      {message.text}
                                    </div>
                                  )}

                                  {message.attachments?.map((attachment, index) => {
                                    const imageData = attachment?.image_data;

                                    if (!imageData?.url) return null;

                                    return (
                                      <div
                                        key={`${message.id}-attachment-${index}`}
                                        className={styles.sharedPostAttachment}
                                      >
                                        <img
                                          src={imageData.url}
                                          alt="Shared Instagram post"
                                          className={styles.sharedPostImage}
                                        />
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>

                              {message.isLastInGroup && (
                                <div
                                  className={`${styles.messageStatusRow} ${isMine
                                      ? styles.messageStatusRowMine
                                      : styles.messageStatusRowTheirs
                                    }`}
                                >
                                  {isPending ? (
                                    <>
                                      <FiClock className={styles.spinIcon} />
                                      <span
                                        className={styles.messageStatusText}
                                      >
                                        Sending...
                                      </span>
                                    </>
                                  ) : isFailed ? (
                                    <>
                                      <FiAlertCircle />
                                      <span
                                        className={styles.messageFailedText}
                                      >
                                        Not delivered
                                      </span>
                                      <button
                                        type="button"
                                        className={styles.retryLink}
                                        onClick={() =>
                                          handleRetryMessage(
                                            selectedConversation.id,
                                            message.id,
                                            message.text,
                                          )
                                        }
                                      >
                                        Retry
                                      </button>
                                    </>
                                  ) : (
                                    <span
                                      className={styles.messageTime}
                                      title={formatFullTimestamp(
                                        message.created_time,
                                      )}
                                    >
                                      {formatTimeOnly(message.created_time)}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    })}

                    <div ref={messagesEndRef} />
                  </div>

                  <div className={styles.replyBoxConversation}>
                    <textarea
                      ref={textareaRef}
                      className={styles.replyInput}
                      rows={1}
                      placeholder={`Send a manual DM reply to @${selectedConversation.name}...`}
                      value={replyInputs[selectedConversation.id] || ""}
                      onChange={(e) => {
                        handleReplyChange(
                          selectedConversation.id,
                          e.target.value,
                        );
                        autoGrowTextarea();
                      }}
                      onKeyDown={(e) =>
                        handleComposerKeyDown(e, selectedConversation.id)
                      }
                      maxLength={2000}
                    />

                    <button
                      type="button"
                      className={styles.sendMessageButton}
                      onClick={() => handleSendMessage(selectedConversation.id)}
                      disabled={
                        !(replyInputs[selectedConversation.id] || "").trim()
                      }
                    >
                      <FiSend />
                    </button>
                  </div>
                </>
              )}
            </section>
          </div>
        </div>
      </main>
    </DashboardLayout>
  );
};

export default InstagramConversation;